import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Table, Button, Spinner, Breadcrumb } from 'react-bootstrap';
import { GiTeacher, GiArchiveResearch } from 'react-icons/gi';
import { FaUserGraduate, FaArrowRight, FaDownload, FaSchool, FaHandsHelping } from 'react-icons/fa';
import {
    LuSprout, LuTrees, LuHeartHandshake, LuCpu, LuRecycle, LuTrash2,
    LuClipboardList, LuBell, LuChevronLeft, LuArrowUpRight, LuLayoutDashboard,
    LuShieldCheck, LuMapPin, LuGlobe,
} from 'react-icons/lu';
import { toast } from 'react-toastify';
import axiosConn from 'config/axiosConn';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useSelector } from 'react-redux';
import { useLocation, Link, useNavigate } from 'react-router-dom';

import ActivityReports from './ActivityReports';
import BricsReport from "./BricsReport";
import NotificationReport from '../../../Pages/NotificationReport';
import DistrictNotificationReport from '../../../Pages/DistrictNotificationReport';
import "./AdminReportShell.css";
import './EnvironmentDayDashboard.css';

// Pledges run from 15 May 2026; the "After 5 June" toggle scopes them to the
// Ek Ped Maa Ke Naam 3.0 window.
const CAMPAIGN_START = "2026-06-05";

function EnvironmentDayDashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const userData = useSelector(state => state.auth.user);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ totalSchools: 0, totalPledges: 0, totalTeachers: 0, totalStudents: 0 });
    const [stateData, setStateData] = useState([]);
    const [districtData, setDistrictData] = useState([]);

    const [view, setView] = useState('national');
    const [selectedState, setSelectedState] = useState('');
    const [downloadingReport, setDownloadingReport] = useState(false); // kept for API compatibility
    // Pledge date scope: false → all pledges (since 15 May); true → only from 5 June.
    const [pledgeAfterJune5, setPledgeAfterJune5] = useState(false);
    // Nothing heavy auto-loads any more: the admin picks a report and only that
    // one mounts (Ek Ped 3.0 and the pledge snapshot are expensive queries).
    const [openReport, setOpenReport] = useState("brics");
    const [sideCollapsed, setSideCollapsed] = useState(false);
    // Notification report drill-down, kept inside the shell: null = the
    // State/UT/AB list, otherwise the district view for that State/UT/AB.
    const [notifState, setNotifState] = useState(null);

    const fetchData = useCallback(async (stateName = null) => {
        setLoading(true);
        try {
            const body = stateName ? { state: stateName } : {};
            // "After 5 June" toggle → scope pledges to the 3.0 window; otherwise
            // (default) show all pledge data since 15 May.
            if (pledgeAfterJune5) body.fromDate = CAMPAIGN_START;
            const response = await axiosConn('post', 'environment-day-stats', body);

            if (response.data && response.data.success) {
                let tStats = response.data.totalStats || {};
                let gStats = response.data.groupedStats || [];

                gStats = gStats.map(item => ({
                    ...item,
                    totalSchools: item.totalPledges,
                    totalPledges: (item.totalTeachers || 0) + (item.totalStudents || 0)
                }));

                tStats = {
                    ...tStats,
                    totalSchools: tStats.totalPledges,
                    totalPledges: (tStats.totalTeachers || 0) + (tStats.totalStudents || 0)
                };

                setStats(tStats);

                if (!stateName) {
                    setStateData(gStats);
                } else {
                    setDistrictData(gStats);
                }
            } else {
                toast.error("Failed to fetch pledge data.");
            }
        } catch (error) {
            console.error(error);
            toast.error("Error fetching data from server.");
        } finally {
            setLoading(false);
        }
    }, [pledgeAfterJune5]);

    useEffect(() => {
        const initializeDashboard = async () => {
            // Derive state if missing and user is state admin (Role 4)
            let stateFromUser = userData?.state;
            if (!stateFromUser && userData?.role === 4 && userData?.username) {
                const stateMap = {
                    'andhrapradesh': 'ANDHRA PRADESH',
                    'arunachalpradesh': 'ARUNACHAL PRADESH',
                    'assam': 'ASSAM',
                    'bihar': 'BIHAR',
                    'chhattisgarh': 'CHHATTISGARH',
                    'goa': 'GOA',
                    'gujarat': 'GUJARAT',
                    'haryana': 'HARYANA',
                    'himachalpradesh': 'HIMACHAL PRADESH',
                    'jammu&kashmir': 'JAMMU & KASHMIR',
                    'jharkhand': 'JHARKHAND',
                    'karnataka': 'KARNATAKA',
                    'kerala': 'KERALA',
                    'madhyapradesh': 'MADHYA PRADESH',
                    'maharashtra': 'MAHARASHTRA',
                    'manipur': 'MANIPUR',
                    'meghalaya': 'MEGHALAYA',
                    'mizoram': 'MIZORAM',
                    'nagaland': 'NAGALAND',
                    'odisha': 'ODISHA',
                    'punjab': 'PUNJAB',
                    'rajasthan': 'RAJASTHAN',
                    'sikkim': 'SIKKIM',
                    'tamilnadu': 'TAMILNADU',
                    'telangana': 'TELANGANA',
                    'tripura': 'TRIPURA',
                    'uttarakhand': 'UTTARAKHAND',
                    'uttarpradesh': 'UTTAR PRADESH',
                    'westbengal': 'West Bengal',
                    'delhi': 'DELHI',
                    'andaman&nicobarislands': 'ANDAMAN & NICOBAR ISLANDS',
                    'chandigarh': 'CHANDIGARH',
                    'damananddiu': 'DADRA & NAGAR HAVELI AND DAMAN & DIU',
                    'lakshadweep': 'LAKSHADWEEP',
                    'puducherry': 'Puducherry',
                    'kvs': 'KENDRIYA VIDYALAYA SANGHATHAN',
                    'nvs': 'NAVODAYA VIDYALAYA SAMITI',
                    'ladakh': 'LADAKH',
                };
                const username = userData.username;
                const stateKey = username.toUpperCase().startsWith('EC4ML_')
                    ? username.split('_')[1].toLowerCase()
                    : '';
                stateFromUser = stateMap[stateKey] || null;
            }

            // If it's a state admin (Role 4), set the view to state directly
            if (userData?.role === 4 && stateFromUser) {
                setSelectedState(stateFromUser);
                setView('state');
            } else if (location.state?.stateValue) {
                const stateValue = location.state.stateValue;
                localStorage.setItem("stateAdminState", stateValue);
                setSelectedState(stateValue);
                setView('state');
            } else if (userData?.role === 4 && userData.state) {
                setSelectedState(userData.state);
                setView('state');
            }
        };

        if (userData) {
            initializeDashboard();
        }
    }, [userData, location.state]);

    useEffect(() => {
        // environment-day-stats is a heavy aggregation — only run it while the
        // pledge section is actually open.
        if (openReport !== "pledge") return;
        if (view === 'national') {
            fetchData();
        } else if (selectedState) {
            fetchData(selectedState);
        }
    }, [fetchData, view, selectedState, openReport]);

    // App shell: `.ard` is fixed below the portal header and only its main pane
    // scrolls, so the shell has to know how tall that header is. `probeRef` is
    // a zero-height div left in the normal flow directly above it — its top is
    // exactly where the page content begins. The portal's logos and web fonts
    // settle after first paint, so it is remeasured on load and on resize.
    const shellRef = useRef(null);
    const probeRef = useRef(null);
    useEffect(() => {
        const measure = () => {
            const shell = shellRef.current;
            const probe = probeRef.current;
            if (!shell || !probe) return;
            const top = probe.getBoundingClientRect().top + window.scrollY;
            shell.style.setProperty("--ard-top", `${Math.max(0, Math.round(top))}px`);
        };
        measure();
        const t1 = window.setTimeout(measure, 120);
        const t2 = window.setTimeout(measure, 600);
        window.addEventListener("resize", measure);
        window.addEventListener("load", measure);
        // the page behind the shell must not scroll, and shows no footer
        document.body.classList.add("ard-shell-page");
        return () => {
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            window.removeEventListener("resize", measure);
            window.removeEventListener("load", measure);
            document.body.classList.remove("ard-shell-page");
        };
    }, []);

    const handleViewDetails = (stateName) => {
        setSelectedState(stateName);
        setView('state');
        fetchData(stateName);
    };

    const handleBreadcrumbClick = () => {
        setView('national');
        setSelectedState('');
        setDistrictData([]);
    };

    const downloadCSV = (data, filename) => {
        const header = ["Name", "Total Schools Participated", "Total Pledge Taken", "Teachers Participated", "Students Participated"];
        const rows = data.map(item => [item.name, item.totalSchools || 0, item.totalPledges, item.totalTeachers, item.totalStudents]);
        const csvContent = "data:text/csv;charset=utf-8,"
            + [header.join(","), ...rows.map(e => e.join(","))].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadPDF = (data, title, isNational = false) => {
        const doc = new jsPDF();
        const firstColName = isNational ? "States/UTs/ABs" : "District";
        const tableColumn = ["#", firstColName, "Total Pledges", "Teachers Participated", "Students Participated"];
        const tableRows = [];

        data.forEach((item, index) => {
            const rowData = [
                index + 1,
                item.name || "Unknown",
                item.totalPledges,
                item.totalTeachers,
                item.totalStudents
            ];
            tableRows.push(rowData);
        });

        const reportTitle = isNational ? "States/UTs/ABs Pledge Report" : `${title} District Report`;
        doc.text(reportTitle, 14, 15);
        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 20,
        });

        const filename = isNational ? "State_Pledge_Report.pdf" : `${title}_District_Report.pdf`;
        doc.save(filename);
    };

    const currentData = view === 'national' ? stateData : districtData;
    const currentStats = stats;

    // Every report an admin can open from here. `to` navigates to its own page;
    // `key` switches the panel rendered below without leaving the dashboard.
    const REPORTS = [
        { key: "brics", Icon: LuSprout, tone: "#23792e", group: "Live campaign", live: true,
          name: "BRICS Sustainable Lifestyle Week", accent: "Lifestyle Week", sub: "1-7 Nov 2026",
          desc: "Save Energy & Adopt a Healthy Lifestyle, reported by schools. Drill from State/UT down to district, block and school.",
          chips: ["New collection", "Refreshes every 2 min", "Excel + PDF"] },
        { key: "ekped", Icon: LuTrees, tone: "#1d8200", group: "Campaign reports",
          name: "Ek Ped Maa Ke Naam 3.0", accent: "3.0", sub: "Plantation & other themes",
          desc: "Every tree-planting and theme activity across the campaign — schools, participants and trees.",
          chips: ["From 5 June 2026", "Excel + PDF"] },
        { key: "pledge", Icon: LuHeartHandshake, tone: "#e07b1e", group: "Campaign reports",
          name: "Pledge Data", accent: "Data", sub: "From 15 May 2026",
          desc: "Mission LiFE pledges taken by schools, students and teachers.", chips: ["Toggle: after 5 June"] },
        { to: "/admin/e-waste-drive", Icon: LuCpu, tone: "#2a6fdb", group: "Closed campaigns",
          name: "E-Waste Collection Drive", sub: "Closed 31 Oct 2026", desc: "", chips: [] },
        { to: "/admin/special-campaign-6-report", Icon: LuRecycle, tone: "#0f9f8f", group: "Closed campaigns",
          name: "National Event 28 Oct (SC6)", sub: "Closed 31 Oct 2026", desc: "", chips: [] },
        { to: "/admin/swachhata-hi-seva", Icon: LuTrash2, tone: "#52a05f", group: "Closed campaigns",
          name: "Swachhata Hi Seva (SWM Drive)", sub: "Closed 20 Oct 2026", desc: "", chips: [] },
        { to: "/admin/swm-report", Icon: LuClipboardList, tone: "#23792e", group: "Closed campaigns",
          name: "SWM Report", sub: "Question-wise", desc: "", chips: [] },
        { key: "notification", Icon: LuBell, tone: "#c2591c", group: "Reference",
          name: "Notification Data", accent: "Data", sub: "Eco Club notifications",
          desc: "Eco Club notifications uploaded by schools, State/UT/AB-wise. Select a row for its districts and the school-wise list.",
          chips: ["Excel"] },
    ];
    const activeReport = REPORTS.find((r) => r.key && r.key === openReport) || null;

    // A title reads better with its tail picked out in the accent colour —
    // "BRICS Sustainable *Lifestyle Week*".
    const heroTitle = (name, accent) =>
        accent && name.endsWith(accent)
            ? <>{name.slice(0, name.length - accent.length)}<em>{accent}</em></>
            : name;

    const isStateAdmin = userData?.role === 4;
    // Who is signed in — shown on the rail and in the hero, because a state
    // admin and the central admin see the same screen with different scope.
    const adminRole = isStateAdmin ? "State Admin" : "Central Admin";
    const adminScope = isStateAdmin
        ? (selectedState || userData?.state || "Your State / UT / AB")
        : (view === 'state' && selectedState ? selectedState : "All States, UTs & ABs");
    // Ek Ped Maa Ke Naam 2.0 data dashboard: central admin → the national snapshot
    // at /Ekpedmaakenaamadmin; state admin → their state 2.0 dashboard.
    const ekped2Route = userData?.role === 1 ? '/Ekpedmaakenaamadmin' : '/Ekpedmaakenaam';

    return (
        <section className="scholdashbrdSec superAdminDashboardSec">
            <Container fluid>
                {/* ── Report shell: a fixed rail of every report, and a hero
                       naming the one on screen and who is signed in ── */}
                {/* measures where the portal header ends — see the effect above */}
                <div ref={probeRef} style={{ height: 0, overflow: "hidden" }} aria-hidden="true" />
                <div className={`ard${sideCollapsed ? " is-collapsed" : ""}`} ref={shellRef}>
                    <aside className="ard-side">
                        <button
                            type="button"
                            className="ard-collapse"
                            onClick={() => setSideCollapsed((v) => !v)}
                            title={sideCollapsed ? "Expand reports" : "Collapse reports"}
                            aria-label={sideCollapsed ? "Expand reports" : "Collapse reports"}
                        >
                            <LuChevronLeft size={16} strokeWidth={2.4} />
                        </button>

                        <div className="ard-side__head">
                            <span className="ard-side__mark"><LuLayoutDashboard size={19} /></span>
                            <span className="ard-side__names">
                                <span className="ard-side__title">Reports</span>
                                <span className="ard-side__eyebrow">Eco Clubs · Mission LiFE</span>
                            </span>
                        </div>

                        <div className="ard-side__inner">
                            {["Live campaign", "Campaign reports", "Closed campaigns", "Reference"].map((group) => (
                                <div className="ard-side__group" key={group}>
                                    <div className="ard-side__label">{group}</div>
                                    {REPORTS.filter((r) => r.group === group).map((r) => (
                                        <button
                                            type="button"
                                            key={r.name}
                                            className={`ard-item${r.key && r.key === openReport ? " is-active" : ""}`}
                                            style={{ "--tone": r.tone }}
                                            title={`${r.name} · ${r.sub}`}
                                            onClick={() => {
                                                if (r.to) return navigate(r.to);
                                                setNotifState(null);
                                                setOpenReport(openReport === r.key ? "" : r.key);
                                            }}
                                        >
                                            <span className="ard-item__ico"><r.Icon size={16} /></span>
                                            <span className="ard-item__text">
                                                <span className="ard-item__name">{r.name}</span>
                                                <span className="ard-item__sub">{r.sub}</span>
                                            </span>
                                            <span className="ard-item__go">{r.to ? <LuArrowUpRight size={14} /> : null}</span>
                                        </button>
                                    ))}
                                </div>
                            ))}
                        </div>

                        <div className="ard-side__who" title={`${adminRole} · ${adminScope}`}>
                            <span className="ard-item__ico"><LuShieldCheck size={16} /></span>
                            <span className="ard-who__text" style={{ minWidth: 0 }}>
                                <span className="ard-who__role">{adminRole}</span>
                                <span className="ard-who__scope">{adminScope}</span>
                            </span>
                        </div>
                    </aside>

                    <div className="ard-main">
                        {activeReport ? (
                            <div className="ard-hero" style={{ "--tone": activeReport.tone }}>
                                <div className="ard-hero__main">
                                    <div className="ard-hero__top">
                                        {activeReport.live && <span className="ard-live"><i />Live</span>}
                                        <span className="ard-hero__kicker">{activeReport.sub}</span>
                                    </div>
                                    <div className="ard-hero__title">{heroTitle(activeReport.name, activeReport.accent)}</div>
                                    {activeReport.desc && <div className="ard-hero__desc">{activeReport.desc}</div>}
                                    <div className="ard-hero__meta">
                                        <span className="ard-chip">{adminScope}</span>
                                        {activeReport.chips.map((c) => <span className="ard-chip" key={c}>{c}</span>)}
                                    </div>
                                </div>
                                <div className="ard-hero__who">
                                    <span className="ard-who__ico">
                                        {isStateAdmin ? <LuMapPin size={19} /> : <LuGlobe size={19} />}
                                    </span>
                                    <span style={{ minWidth: 0 }}>
                                        <span className="ard-who__role" style={{ display: "block" }}>{adminRole}</span>
                                        <span className="ard-who__scope">
                                            <LuShieldCheck size={12} /> Signed in · {adminScope}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="ard-hero">
                                <div className="ard-hero__main">
                                    <div className="ard-hero__top">
                                        <span className="ard-hero__kicker">Eco Clubs for Mission LiFE</span>
                                    </div>
                                    <div className="ard-hero__title">Pick a <em>report</em></div>
                                    <div className="ard-hero__desc">Choose a report on the left. Nothing is loaded until you open it, so the page stays fast.</div>
                                </div>
                                <div className="ard-hero__who">
                                    <span className="ard-who__ico">
                                        {isStateAdmin ? <LuMapPin size={19} /> : <LuGlobe size={19} />}
                                    </span>
                                    <span style={{ minWidth: 0 }}>
                                        <span className="ard-who__role" style={{ display: "block" }}>{adminRole}</span>
                                        <span className="ard-who__scope">
                                            <LuShieldCheck size={12} /> Signed in · {adminScope}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="ard-body">
                {openReport === "brics" && (
                <div className="mt-3">
                    <BricsReport embedded />
                </div>
                )}

                {openReport === "notification" && (
                <div className="mt-2 ard-embed">
                    {notifState ? (
                        <DistrictNotificationReport
                            embedded
                            stateName={notifState.name}
                            data={notifState.data}
                            onBack={() => setNotifState(null)}
                        />
                    ) : (
                        <NotificationReport
                            embedded
                            onOpenState={(name, data) => setNotifState({ name, data })}
                        />
                    )}
                </div>
                )}

                {openReport === "ekped" && (
                <div className="mt-3">
                    <ActivityReports embedded />
                </div>
                )}

                {/* ── 4. Pledge data (from 15 May) with the "After 5 June" toggle ── */}
                {openReport === "pledge" && (
                <div id="env-pledge-snapshot" className="dashbrdTopSrchDiv lavel mt-4 mb-1" style={{ position: "relative", zIndex: 1 }}>
                    <h2 style={{ color: 'orange' }}>🤝 Pledge Data {view === 'national' ? '· ALL STATES' : `· ${selectedState}`}</h2>
                    <p style={{ margin: "4px 0 10px", color: "#555" }}>
                        {pledgeAfterJune5
                            ? "Showing pledges submitted from 5 June 2026 onward."
                            : "Showing all pledges since the campaign began (15 May 2026)."}
                    </p>

                    {/* 5 June toggle */}
                    <div className="d-flex justify-content-center gap-2 mb-3 flex-wrap">
                        <Button
                            size="sm"
                            variant={!pledgeAfterJune5 ? 'success' : 'outline-success'}
                            onClick={() => setPledgeAfterJune5(false)}
                            style={{ borderRadius: 20, fontWeight: 600 }}
                        >
                            All (since 15 May)
                        </Button>
                        <Button
                            size="sm"
                            variant={pledgeAfterJune5 ? 'success' : 'outline-success'}
                            onClick={() => setPledgeAfterJune5(true)}
                            style={{ borderRadius: 20, fontWeight: 600 }}
                        >
                            After 5 June
                        </Button>
                    </div>

                    {view === 'national' && (
                        <div className="text-end d-flex justify-content-end gap-2 flex-wrap align-items-center">
                            <Button onClick={() => downloadCSV(stateData, "States_UTs_ABs_Pledge_Report.csv")} className="btn-success" style={{ borderRadius: '20px' }}>
                                <FaDownload size={20} color="#dddddd" className="me-2" /> Download Report
                            </Button>
                            <Button onClick={() => downloadPDF(stateData, "State", true)} className="btn-danger" style={{ borderRadius: '20px' }}>
                                <FaDownload size={20} color="#dddddd" className="me-2" /> Download PDF
                            </Button>
                            <Link to="/admin/themes-analytics">
                                <Button className="btn-info mt-2 mt-md-0" style={{ background: 'linear-gradient(45deg, #17a2b8, #117a8b)', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', borderRadius: '20px', transition: 'transform 0.2s', color: 'white', fontWeight: 'bold' }}>
                                    📊 View Themes Analytics Dashboard
                                </Button>
                            </Link>
                        </div>
                    )}
                </div>
                )}

                {view === 'state' && userData?.role !== 4 && (
                    <div className="breadcrumb-section mt-1 text-center">
                        <Button variant="outline-success" size="sm" onClick={handleBreadcrumbClick}>
                            &larr; Back to National Dashboard
                        </Button>
                    </div>
                )}

                {/* Pledge figures only — `loading` starts true and is cleared by
                    fetchData, which now runs only while this section is open, so
                    the spinner must be scoped to it or it never goes away. */}
                {openReport === "pledge" && (loading ? (
                    <div className="loading-state text-center mt-5">
                        <Spinner animation="border" variant="success" />
                        <p>Loading analytics data...</p>
                    </div>
                ) : (
                    <>
                        <div className="dashbrdTopSrchDivlavel mt-2 mb-0" style={{ position: "relative", zIndex: 0 }}>
                            <div className="d-flex justify-content-center flex-wrap">
                                <div className="boxdiv boxdivFour m-2">
                                    <div className="d-flex justify-content-between p-3">
                                        <div>
                                            <p> TOTAL SCHOOL <span className="text-success w100">PARTICIPATED</span></p>
                                        </div>
                                        <FaSchool size={82} color="#839c30" />
                                    </div>
                                    <div className="d-flex justify-content-center bg-success bottomBox">
                                        <div className="bgdanger w-100">
                                            <p>TOTAL</p>
                                            <h2>{currentStats.totalSchools || 0}</h2>
                                        </div>
                                    </div>
                                </div>
                                <div className="boxdiv boxdivOne m-2">
                                    <div className="d-flex justify-content-between p-3">
                                        <div>
                                            <p> TOTAL PLEDGES <span className="text-success w100">TAKEN</span></p>
                                        </div>
                                        <FaHandsHelping size={82} color="#969640" />
                                    </div>
                                    <div className="d-flex justify-content-center bg-success bottomBox">
                                        <div className="bgdanger w-100">
                                            <p>TOTAL</p>
                                            <h2>{currentStats.totalPledges}</h2>
                                        </div>
                                    </div>
                                </div>
                                <div className="boxdiv boxdivTwo m-2">
                                    <div className="d-flex justify-content-between p-3">
                                        <div >
                                            <p>TEACHERS <span className="text-success w100">PARTICIPATED</span></p>
                                        </div>
                                        <GiTeacher size={82} color="#9c6829" />
                                    </div>
                                    <div className="d-flex justify-content-between bg-success bottomBox">
                                        <div className="bgdanger w-100">
                                            <p>TOTAL</p>
                                            <h2>{currentStats.totalTeachers}</h2>
                                        </div>
                                    </div>
                                </div>

                                <div className="boxdiv boxdivThree m-2">
                                    <div className="d-flex justify-content-between p-3">
                                        <div >
                                            <p>STUDENTS <span className="text-success w100">PARTICIPATED</span></p>
                                        </div>
                                        <FaUserGraduate size={82} color="#9f4d9b" />
                                    </div>
                                    <div className="d-flex justify-content-between bg-success bottomBox">
                                        <div className="bgdanger w-100">
                                            <p>TOTAL</p>
                                            <h2>{currentStats.totalStudents}</h2>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="dashbrdTopSrchDiv activityListNewDiv mt-4 text-center">
                            <h4>{view === 'national' ? 'States/UTs/ABs-wise Stats' : 'District-wise Stats'}</h4>
                            {view === 'state' && (
                                <div className="text-end mb-2 d-flex justify-content-end gap-2 flex-wrap align-items-center">
                                    {/* Themes Analysis Dashboard button — shown to all state-view users including role 4 */}
                                    <Button
                                        onClick={() => navigate("/admin/state/themes-analysis")}
                                        style={{
                                            background: "linear-gradient(135deg, #0d9488 0%, #2563eb 100%)",
                                            border: "none",
                                            color: "#fff",
                                            fontWeight: 600,
                                            padding: "7px 16px",
                                            borderRadius: 20,
                                            boxShadow: "0 4px 14px rgba(13,148,136,0.35)",
                                            fontSize: 13,
                                        }}
                                    >
                                        📊 View Themes Analysis Dashboard
                                    </Button>
                                    <Button size="sm" onClick={() => downloadCSV(districtData, `${selectedState}_District_Report.csv`)}>
                                        <FaDownload className="me-1" /> Export District Data
                                    </Button>
                                    <Button size="sm" variant="danger" onClick={() => downloadPDF(districtData, selectedState)}>
                                        <FaDownload className="me-1" /> Download PDF
                                    </Button>
                                </div>
                            )}
                            <div className="table-responsive">
                                <Table hover className="env-table text-center table-bordered mt-3">
                                    <thead>
                                        <tr style={{ backgroundColor: '#e2f5e9', color: '#155724', fontWeight: 'bold' }}>
                                            <th style={{ backgroundColor: '#e2f5e9', color: '#155724' }}>#</th>
                                            <th style={{ backgroundColor: '#e2f5e9', color: '#155724' }}>{view === 'national' ? 'STATE' : 'DISTRICT'}</th>
                                            <th style={{ backgroundColor: '#e2f5e9', color: '#155724' }}>TOTAL SCHOOLS PARTICIPATED</th>
                                            <th style={{ backgroundColor: '#e2f5e9', color: '#155724' }}>TOTAL PLEDGES TAKEN</th>
                                            <th style={{ backgroundColor: '#e2f5e9', color: '#155724' }}>TEACHERS PARTICIPATED</th>
                                            <th style={{ backgroundColor: '#e2f5e9', color: '#155724' }}>STUDENTS PARTICIPATED</th>
                                            <th style={{ backgroundColor: '#e2f5e9', color: '#155724' }}>ACTION</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(view === 'national' ? stateData : districtData).length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="text-center empty-state">
                                                    <GiArchiveResearch size={40} className="mb-2 text-muted" />
                                                    <p>No participation data available.</p>
                                                </td>
                                            </tr>
                                        ) : (
                                            (view === 'national' ? stateData : districtData).map((item, idx) => (
                                                <tr key={idx} onClick={() => view === 'national' && handleViewDetails(item.name)}>
                                                    <td>{idx + 1}</td>
                                                    <td className="fw-bold text-success">{item.name || "Unknown"}</td>
                                                    <td>{item.totalSchools || 0}</td>
                                                    <td>{item.totalPledges}</td>
                                                    <td>{item.totalTeachers}</td>
                                                    <td>{item.totalStudents}</td>
                                                    {view === 'national' ? (
                                                        <td>
                                                            <Button
                                                                variant="outline-success"
                                                                className="btn-view-details"
                                                                onClick={(e) => { e.stopPropagation(); handleViewDetails(item.name); }}
                                                            >
                                                                View Details <FaArrowRight className="ms-1" />
                                                            </Button>
                                                        </td>
                                                    ) : (
                                                        <td>
                                                            <Button
                                                                variant="outline-primary"
                                                                className="btn-view-schools"
                                                                onClick={(e) => { e.stopPropagation(); navigate('/view-schools', { state: { state: selectedState, district: item.name, theme: 'environment-day' } }); }}
                                                            >
                                                                View Schools <FaArrowRight className="ms-1" />
                                                            </Button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </>
                ))}
                        </div>{/* .ard-body */}
                    </div>{/* .ard-main */}
                </div>{/* .ard */}

                {/* ── 5. Ek Ped Maa Ke Naam 2.0 (legacy campaign) — moved from the
                       top of the page so 3.0 data leads the dashboard ── */}
                <div className="d-flex justify-content-center my-4">
                    <Button
                        onClick={() => navigate(ekped2Route)}
                        style={{ background: 'linear-gradient(45deg, #0d9488, #0f766e)', border: 'none', borderRadius: 20, fontWeight: 600 }}
                    >
                        🌱 View Ek Ped Maa Ke Naam 2.0
                    </Button>
                </div>
            </Container>
        </section>
    );
}

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });
        console.error("ErrorBoundary caught an error", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '20px', background: '#ffebee', color: '#c62828' }}>
                    <h2>Something went wrong in EnvironmentDayDashboard.</h2>
                    <details style={{ whiteSpace: 'pre-wrap' }}>
                        {this.state.error && this.state.error.toString()}
                        <br />
                        {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </details>
                </div>
            );
        }
        return this.props.children;
    }
}

export default function EnvironmentDayDashboardWithErrorBoundary(props) {
    return (
        <ErrorBoundary>
            <EnvironmentDayDashboard {...props} />
        </ErrorBoundary>
    );
}
